/* eslint-disable max-statements */

import { LoadingOverlay, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useState } from 'react';

import { TransitDocumentsEnum } from '@e-origin/shared';

import { AsyncAutocomplete, DatePickerInput, MultiLangCodes, TextInput } from '@components';
import { useDeclarant } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { FormDocumentsInputType, TransitDocumentFormSchema } from './TransitDocumentFormSchema';

interface TransitDocumentFormFactoryProps {
  type: TransitDocumentsEnum;
  editDisabled: boolean;
  form: UseFormReturnType<any>;
  forGoodsItem?: boolean;
  isArrival: boolean;
}

export const TransitDocumentFormFactory: React.FC<TransitDocumentFormFactoryProps> = ({ type, editDisabled, form, forGoodsItem, isArrival }) => {
  const { data: declarant, isLoading } = useDeclarant();
  const [formSchema, setFormSchema] = useState({} as any);
  const codelistSystems = useCodeListSystem({ isTransit: true });

  useEffect(() => {
    setFormSchema(TransitDocumentFormSchema[type]);
  }, [type]);

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack>
      {formSchema?.fields &&
        Object.keys(formSchema.fields).map((path, idx) => {
          const { type: fieldType, placeholder, show, code, 'data-pw': dataPw, disabled } = formSchema.fields[path];

          if (show && !show(isArrival, forGoodsItem)) {
            return null;
          }

          if (fieldType === FormDocumentsInputType.AUTOCOMPLETE) {
            return (
              <AsyncAutocomplete
                {...form.getInputProps(path)}
                key={`${type}-${idx}`}
                fetchOptions={(search) => formSchema.fields[path].search(search, declarant.language)}
                placeholder={placeholder}
                disabled={editDisabled || disabled?.(isArrival)}
              />
            );
          }

          if (fieldType === FormDocumentsInputType.MULTI_LANG) {
            return (
              <MultiLangCodes
                {...form.getInputProps(path)}
                key={`${type}-${idx}-${code}`}
                label={placeholder}
                code={code}
                system={codelistSystems}
                disabled={editDisabled || disabled?.(isArrival)}
                data-pw={dataPw}
              />
            );
          }

          if (fieldType === FormDocumentsInputType.INPUT) {
            return (
              <TextInput
                key={`${type}-${idx}`}
                {...form.getInputProps(path)}
                label={placeholder}
                disabled={editDisabled || disabled?.(isArrival)}
                data-pw={dataPw}
              />
            );
          }

          if (fieldType === FormDocumentsInputType.DATE) {
            return (
              <DatePickerInput key={`${type}-${idx}`} {...form.getInputProps(path)} label={placeholder} disabled={editDisabled || disabled?.(isArrival)} />
            );
          }

          return null;
        })}
    </Stack>
  );
};
